/**
 * ============================================================================
 * MASTER SERVICE: Role & Permissions Service (`tbl_roles`, `tbl_role_operations`)
 * ============================================================================
 * Purpose:
 *   Handles API requests for role management, hierarchy suggestion algorithms,
 *   hierarchy trees, and bulk operation permission assignments.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import api from "../../core/services/api";
import { createCrudService } from "../../core/services/crudServiceFactory";

const baseRoleService = createCrudService("roles");

const roleService = {
  ...baseRoleService,

  /**
   * Suggests next available hierarchy level for a new role.
   *
   * @param {Object} [params] - Query parameters
   * @returns {Promise<Object>} Suggested hierarchy level
   */
  suggestHierarchy: (params = {}) => {
    return api.get("/roles/hierarchy/suggest", { params });
  },

  /**
   * Retrieves full hierarchical role tree.
   *
   * @returns {Promise<Object>} Tree of roles organized by level
   */
  getHierarchyTree: () => {
    return api.get("/roles/hierarchy/tree");
  },

  /**
   * Retrieves list of permissions assigned to a specific role ID.
   *
   * @param {number} roleId
   * @returns {Promise<Object>} List of role operations
   */
  getRolePermissions: (roleId) => {
    return api.get(`/roles/${roleId}/permissions`);
  },

  /**
   * Bulk assigns operation permissions to a specific role ID.
   *
   * @param {number} roleId
   * @param {number[]} operationIds - Array of operation IDs
   * @returns {Promise<Object>} Assignment confirmation
   */
  assignPermissions: (roleId, operationIds) => {
    return api.post(`/roles/${roleId}/permissions`, { operationIds });
  },
};

export default roleService;
